import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'reactstrap';

import apiOffers from 'api/offers';
import IOfferProductGet from 'api/interfaces/offers/IOfferProductGet';
import ISearchedPosition from 'api/interfaces/offers/ISearchedPosition';
import { OfferCurrencyProvider } from 'services/offers/currency';
import translations from 'services/translations/translations';

import CalculationWindow from '../offerProduct/calculationWindow/CalculationWindow';
import formatToFormStateFormat from '../offerProduct/formatToFormStateFormat';
import IFormState from '../offerProduct/IFormState';
import views from '../offerProduct/views';
import PositionPreview from './PositionPreview';

/**
 * The whole calculation of a position found by the position search, as it is in the
 * calculation window of its offer but read only: all alternatives, rows, details,
 * installations, expenses, margins and texts. If it cannot be loaded, the summary of the
 * search results is shown instead. Below, back to the results or copy to the offer.
 */
const PositionCalculationPreview = ({
  copyLabel,
  copying,
  onBack,
  onCopy,
  position,
}: {
  copyLabel: string;
  copying: boolean;
  onBack: () => void;
  onCopy: () => void;
  position: ISearchedPosition;
}): JSX.Element => {
  const { t } = useTranslation([translations.offers]);
  const [loaded, setLoaded] = useState<IOfferProductGet | null>(null);
  const [formState, setFormState] = useState<IFormState | null>(null);
  const [failed, setFailed] = useState(false);
  // The window changes its view e.g. for attaching a configuration; read only it stays
  const [view, setView] = useState(views.calculationWindow);

  useEffect(() => {
    let cancelled = false;
    apiOffers.products
      .get(position.offerProductId)
      .then((product: IOfferProductGet) => {
        if (!cancelled) {
          setLoaded(product);
          setFormState(formatToFormStateFormat(product));
        }
      })
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
    };
  }, [position.offerProductId]);

  // Without the whole calculation, the summary of the search results
  if (failed) {
    return (
      <PositionPreview
        copyLabel={copyLabel}
        copying={copying}
        onBack={onBack}
        onCopy={onCopy}
        position={position}
      />
    );
  }

  return (
    <div
      aria-label={`${position.position} ${position.name}`}
      className="position-calculation-preview"
      role="dialog"
    >
      <div className="position-calculation-preview-body">
        {loaded === null || formState === null ? (
          <p className="position-calculation-preview-loading">
            {t(`${translations.offers}:positionSearch.Loading calculation`)}
          </p>
        ) : (
          <OfferCurrencyProvider
            additionalOfferId={loaded.additionalOffer?.id ?? null}
            offerId={loaded.offer?.id ?? null}
          >
            {/* Read only: changes are not saved anywhere */}
            <CalculationWindow
              close={onBack}
              formState={formState}
              locked
              offerProductId={position.offerProductId}
              offerType={position.offerType}
              save={() => undefined}
              setFormState={setFormState}
              setView={(next) =>
                setView(
                  next === views.offerProduct ? views.calculationWindow : next
                )
              }
              view={view}
            />
          </OfferCurrencyProvider>
        )}
      </div>
      <footer>
        <span className="position-calculation-preview-source">
          {position.targetName}
          {position.revisionNumber > 1 ? ` (v${position.revisionNumber})` : ''}
          {position.organizationUnit ? ` · ${position.organizationUnit}` : ''}
        </span>
        <Button className="modal-button" onClick={onBack}>
          {t(`${translations.offers}:positionSearch.Back`)}
        </Button>
        <Button className="modal-button" disabled={copying} onClick={onCopy}>
          {copyLabel}
        </Button>
      </footer>
    </div>
  );
};

export default PositionCalculationPreview;
